import type { Except } from "type-fest";
import { z } from "zod";
import type { Price } from "../../shared/prices/price";
import { priceZodSchema } from "../../shared/prices/price";
import type { IsoAndUnixTimestamp } from "../date-time/iso-and-unix";
import { isoAndUnixZodSchema, nowIsoAndUnix } from "../date-time/iso-and-unix";
import type { DurationInMinutes } from "../date-time/time-spans";
import type { ProductId } from "../products/product-id";
import { productIdZodSchema } from "../products/product-id";
import type { Amount } from "../units-and-quantities/amount";
import { amountZodSchema } from "../units-and-quantities/amount";
import type { BookableId } from "./bookable-id";
import { bookableIdFactory, bookableIdZodSchema } from "./bookable-id";
import type { BookableSettings } from "./bookable-settings";
import { bookableSettingsZodSchema } from "./bookable-settings";
import type { BookableType } from "./bookable-type";
import { bookableTypes } from "./bookable-type";
import type { ContentBlock } from "./content-block";
import { contentBlockZodSchema } from "./content-block";
import type { ExtrasGroup } from "./extras-group";
import type { RReferences } from "./resource-references/resource-reference-group";
import { rReferencesZodSchema } from "./resource-references/resource-reference-group";
import type { BookableSession } from "./sessions/bookable-session";
import { bookableSessionZodSchema } from "./sessions/bookable-session";

type BookableBase = {
    readonly _id: BookableId;
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly _type: "BOOKABLE";
    readonly price: Price;
    /**
     * Amount of stock item required for 1 sku \
     *
     * E.g. A shirt could have 5 pcs in stock \
     * A sku could be a two-pack of shirts. \
     * When you buy 1 of the sku, the amount (d) is 2 \
     * Two shirts in stock is required to buy one of the sku
     * @type {Amount}
     */
    readonly amount: Amount;
    readonly type?: BookableType;
    /** @deprecated finns inget alternativ men snart */
    readonly sessions: BookableSession[] | undefined;
    readonly resources: RReferences[];
    readonly main: RReferences;
    readonly extras: ProductId[];
    readonly extrasGroups?: ExtrasGroup[];
    readonly contentBlocks: ContentBlock[];
    readonly settings: BookableSettings;

    /**
     * Product gives door access to order.
     *
     * @type {boolean}
     */
    readonly hasAccess: boolean;
};

type BookableGeneric = BookableBase & {
    readonly type?: "GENERIC";
};

type BookableTableBooking = BookableBase & {
    readonly type: "TABLE_BOOKING";
    /**
     * The amount of people that can be booked at the same time to not overflow the kitchen
     */
    readonly maxGuestsPerSession?: number;
    /**
     * Are customers allowed to cancel their reservation themselves using the app without contacting the provider
     * @type {boolean}
     */
    readonly cancellationByCustomerAllowed?: boolean;
    /**
     * How far ahead can a customer cancel a table reservation themselves
     * @type {DurationInMinutes}
     */
    readonly cancellationByCustomerTimeLimit?: DurationInMinutes;
};

type RentBy = "HOUR" | "DAY" | "NIGHT";

type BookableRentalBooking = BookableBase & {
    readonly type: "RENTAL";
    /**
     * Limit the end date of the rental to match with another session
     */
    spanning?: boolean;
    rentBy?: RentBy;
};

type Bookable = BookableGeneric | BookableTableBooking | BookableRentalBooking;

const bookableZodSchema: z.ZodSchema<Bookable> = z.object({
    _id: bookableIdZodSchema,
    _type: z.literal("BOOKABLE"),
    _createdAt: isoAndUnixZodSchema,
    price: priceZodSchema,
    amount: amountZodSchema,
    minQuantity: z.number().nullable(),
    type: z.enum(bookableTypes).optional(),
    sessions: z
        .array(bookableSessionZodSchema)
        .optional() as unknown as z.ZodSchema<BookableSession[] | undefined>,
    resources: z.array(rReferencesZodSchema),
    main: rReferencesZodSchema,
    extras: z.array(productIdZodSchema),
    extrasGroups: z.array(z.any()).optional(),
    contentBlocks: z.array(contentBlockZodSchema),
    settings: bookableSettingsZodSchema,
    hasAccess: z.boolean(),
}) as z.ZodSchema<Bookable>; // put it here because zod can't handle <type> | undefined, only optionals (session prop)

const getBookableResourceReferences = (bookable: Bookable) => {
    return [
        ...getResourceReferences(bookable.main),
        ...bookable.resources.flatMap(getResourceReferences),
    ];
};
const getResourceReferences = (rr: RReferences) => {
    if (rr._type === "RESOURCE_REFERENCE") return [rr];
    return rr.references;
};

type FactoryParams = Except<Bookable, "_createdAt" | "_type" | "_id">;
const bookableFactory = (params: FactoryParams): Bookable => {
    return {
        ...params,
        _id: bookableIdFactory(),
        _createdAt: nowIsoAndUnix(),
        _type: "BOOKABLE",
    };
};

export {
    bookableFactory,
    bookableZodSchema as bookableZodObject,
    getBookableResourceReferences,
    getResourceReferences,
};
export type {
    Bookable,
    BookableBase,
    BookableRentalBooking,
    BookableTableBooking,
    RentBy,
};
